import Investor from '../../models/Investor';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import { buildEntityChatDeepLink, buildInvestorChatDeepLink } from '../chatDeepLinks';
import { debugChatLinks } from '../chatLinkDebug';
import { collectPhrases, expandFundMatchPhrases, normalizePhrase } from './phraseUtils';
import { appendSignaturePhrases, appendSignatureSuffixPhrases } from './signaturePhrases';
import type { ChatLinkTarget } from './types';

function investmentDetails(investor: Investor): {
  label: string | null;
  amount: number | null;
} {
  if (investor.status === 'CANDIDATE') {
    return { label: null, amount: null };
  }
  if (investor.status === 'LEAD') {
    return investor.eoiSubmitted
      ? { label: 'Proposed Investment', amount: investor.indicativeInvestment ?? null }
      : { label: null, amount: null };
  }
  if (investor.status === 'OPPORTUNITY') {
    return { label: 'Preferred Investment', amount: investor.preferredInvestment ?? null };
  }
  return { label: 'Committed Investment', amount: investor.committedInvestment ?? null };
}

function formatStatus(status: Investor['status']): string {
  return status.replace(/_/g, ' ');
}

export function buildChatLinkCorpus(
  investments: Investor[],
  entities: InvestmentEntity[],
  conversationId: string,
): ChatLinkTarget[] {
  const targets: ChatLinkTarget[] = [];

  for (const investor of investments) {
    const fund = investor.fundOpportunity;
    const fundOpportunityExternalId = investor.fundOpportunityId ?? fund?.id;
    if (!fundOpportunityExternalId) continue;

    const fundName = normalizePhrase(fund?.name ?? '');
    const summaryTitle = normalizePhrase(fund?.summaryTitle ?? '');
    const matchPhrases = collectPhrases(...expandFundMatchPhrases(fundName), summaryTitle);
    if (matchPhrases.length === 0) continue;

    const investment = investmentDetails(investor);
    targets.push({
      id: `investor:${investor.id}`,
      type: 'fund_investment',
      matchPhrases,
      displayName: fundName || summaryTitle || 'Fund investment',
      subtitle: formatStatus(investor.status),
      navigateUrl: buildInvestorChatDeepLink(
        fundOpportunityExternalId,
        investor.id,
        investor.status,
        conversationId,
      ),
      card: {
        fundName: fundName || summaryTitle || undefined,
        status: formatStatus(investor.status),
        investmentLabel: investment.label ?? undefined,
        investmentAmount: investment.amount,
        fundOpportunityExternalId,
        investorExternalId: investor.id,
        investorStatus: investor.status,
        eoiSubmitted: investor.eoiSubmitted,
        indicativeInvestment: investor.indicativeInvestment,
        preferredInvestment: investor.preferredInvestment,
        committedInvestment: investor.committedInvestment,
        fundSize: fund?.fundSize ?? null,
      },
    });
  }

  for (const entity of entities) {
    const matchPhrases = collectPhrases(entity.name, entity.companyName, entity.trustName);
    if (matchPhrases.length === 0) continue;

    targets.push({
      id: `entity:${entity.id}`,
      type: 'investment_entity',
      matchPhrases,
      displayName: entity.name,
      subtitle: entity.type.replace(/_/g, ' '),
      navigateUrl: buildEntityChatDeepLink(entity.id, conversationId),
      card: {
        entityType: entity.type.replace(/_/g, ' '),
        entityTypeCode: entity.type,
        entityExternalId: entity.id,
        entityName: entity.name,
      },
    });
  }

  appendSignaturePhrases(targets);
  appendSignatureSuffixPhrases(targets);

  // Fund investments win over entities when phrases overlap (funds listed first).
  debugChatLinks('corpus built', {
    targetCount: targets.length,
    targets: targets.map((target) => ({
      id: target.id,
      type: target.type,
      matchPhrases: target.matchPhrases,
      navigateUrl: target.navigateUrl,
    })),
  });
  return targets;
}
